// dependencies
import { format, isDate } from "date-fns";
import * as React from "react";

// utils
import { cn } from "../../../lib/utils";

// components
import { Text } from "../../atoms/Text";
// types
import type { TextProps } from "../../atoms/Text";

const dateSizeOptions = ["small", "medium", "large"] as const;

type DateSizeOption = (typeof dateSizeOptions)[number];

type BaseTextDateProps = Partial<Omit<TextProps, "children">> & {
	/**
	 * The date object to format
	 */
	date: Date;
	/**
	 * The length of the resulting formatted date/datetime string
	 * @default "small"
	 */
	dateSize?: DateSizeOption;
};

type TextDatePropsWithTime = BaseTextDateProps & {
	/**
	 * Whether the resulting formatted date/datetime string has a time component
	 * @default true
	 */
	hasTime: true;
	/**
	 * If true, adds a bullet delimiter to separate the date and time components
	 * @default false
	 */
	hasDelimiter?: boolean;
	/**
	 * If true, removes the space between the time and am/pm (e.g. "12:00pm" instead of "12:00 pm")
	 * @default false
	 */
	compactTime?: boolean;
};

type TextDatePropsWithoutTime = BaseTextDateProps & {
	/**
	 * Whether the resulting formatted date/datetime string has a time component
	 * @default true
	 */
	hasTime?: false;
};

export type TextDateProps = TextDatePropsWithTime | TextDatePropsWithoutTime;

/**
 * Helper function to return a date-fns format string using provided parameters
 *
 * @param hasTime A boolean value representing whether the output format has a time component
 * @param hasDelimiter A boolean value to add bullet delimiter between date and time if hasTime is true
 * @param dateSize A string value for the resulting format string's length
 * @returns a date-fns date formatting string
 */
const getDateFormatString = ({
	hasTime,
	hasDelimiter,
	dateSize,
	compactTime,
}: {
	hasTime: boolean;
	hasDelimiter?: boolean;
	dateSize: DateSizeOption;
	compactTime?: boolean;
}) => {
	let dateFormat: string;
	switch (dateSize) {
		case "small":
			dateFormat = "M/d/yy";
			break;
		case "medium":
			dateFormat = "MMM d, yyyy";
			break;
		case "large":
			dateFormat = "MMMM d, yyyy";
			break;
		default:
			dateFormat = "M/d/yy";
	}

	if (!hasTime) {
		return dateFormat;
	}

	const delimiterStr = hasDelimiter ? " • " : " ";
	const timeFormat = compactTime ? "h:mmaaa" : "h:mm aaa";

	return `${dateFormat}${delimiterStr}${timeFormat}`;
};

/**
 * Utility function to format a date object into a standardized date or datetime string.
 * Default formatting is a datetime string of small size.
 *
 * @param date A date object to format
 * @param options An object containing formatting options hasTime, dateSize, and hasDelimiter
 * @returns a formatted date/datetime string or null if an invalid date object was provided
 */
export const formatDateTime = (
	date: Date,
	options?: {
		hasTime?: boolean;
		dateSize?: DateSizeOption;
		hasDelimiter?: boolean;
		compactTime?: boolean;
	}
) => {
	const { hasTime = true, dateSize = "small", hasDelimiter = false, compactTime = false } = options || {};

	// Handle invalid date objects passed as a parameter
	if (!isDate(date) || isNaN(date.getTime())) {
		return null;
	}

	const dateFormat = getDateFormatString({ hasTime, hasDelimiter, dateSize, compactTime });
	const formattedDateString = format(date, dateFormat);

	// Return formatted date string using date-fns format function
	return formattedDateString;
};

/**
 * React component to format and display a date object using the Text component
 * @returns a Text component containing the formatted date/datetime string
 */
const TextDate = (props: TextDateProps) => {
	const { date, hasTime = true, dateSize = "small", dangerouslySetClassName, ...textProps } = props;
	let hasDelimiter = false;
	let compactTime = false;

	// Extract hasDelimiter and compactTime if hasTime is true
	if (hasTime) {
		hasDelimiter = (props as TextDatePropsWithTime).hasDelimiter || false;
		compactTime = (props as TextDatePropsWithTime).compactTime || false;
	}

	// Use utility function to obtain formatted date string using props
	const formattedDateString = formatDateTime(date, { hasTime, hasDelimiter, dateSize, compactTime });

	// If returned value is null, return null from component
	if (!formattedDateString) {
		return null;
	}

	return (
		<Text
			{...textProps}
			size={textProps.size ?? "paragraph-3"}
			weight={textProps.weight ?? "regular"}
			dangerouslySetClassName={cn(dangerouslySetClassName)}
		>
			{formattedDateString}
		</Text>
	);
};

export { TextDate };
